import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { QuotationService } from '../../services/quotation.service';
import { SalesOrderService } from '../../services/salesOrder.service';
import { SalesReturnService } from '../../services/salesReturn.service';
import { SalesTargetService } from '../../services/salesTarget.service';
import { SalesMyTarget } from '../../interfaces/sales/salesTarget.interface';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { Quotation, QuotationStatus } from '../../interfaces/sales/quotation.interface';
import { OrderSummary } from '../../interfaces/sales/salesOrder.interface';
import { SalesReturnSummary } from '../../interfaces/sales/salesReturn.interface';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import StatTile from './charts/StatTile';
import HBars from './charts/HBars';
import MyBreakdownsDashSection from '../maintenance/MyBreakdownsDashSection';
import MyWidgetsSection from './board/MyWidgetsSection';

const QUOTE_PAGE_SIZE = 200;
const LIST_LIMIT = 5;

const fmtInt = (n: number) => Math.round(n).toLocaleString('en-US');

const STATUS_LABELS: Record<QuotationStatus, string> = {
    DRAFT: 'Draft', SENT: 'Sent', CONFIRMED: 'Confirmed', REJECTED: 'Rejected', EXPIRED: 'Expired', CONVERTED: 'Converted',
};

function Section({ title, subtitle, children, ...rest }: { title: string; subtitle?: string; children: React.ReactNode; 'data-help-section'?: string }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }} {...rest}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{subtitle}</Typography>}
            <Box sx={{ mt: subtitle ? 0 : 1 }}>{children}</Box>
        </Paper>
    );
}

function Empty({ text }: { text: string }) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{text}</Typography>;
}

function QuotationList({ rows, testId }: { rows: Quotation[]; testId: string }) {
    return (
        <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {rows.slice(0, LIST_LIMIT).map(q => (
                <Stack key={q.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid={`${testId}-${q.id}`}>
                    <Link component={RouterLink} to={`/sales/quotations/${q.id}`} underline="hover" sx={{ fontWeight: 600 }}>
                        {q.quotationNumber || 'Quotation'}
                    </Link>
                    <Typography variant="body2" color="text.secondary">{q.customerName}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        {[formatMoney(q.totalAmount), q.validUntil ? `valid to ${formatDate(q.validUntil)}` : null].filter(Boolean).join(' · ')}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
}

const isThisMonth = (iso?: string): boolean => {
    if (!iso) return false;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
};

/**
 * Sales rep home: quotations, orders and returns. Every block comes from an endpoint a sales
 * role can already reach (no dashboard keys needed) and is fetched independently, so a failure
 * or a missing permission only hides the widgets that depend on it. Quotation figures are
 * computed client-side from the latest page of quotations.
 */
const SalesRepDashboard: React.FC = () => {
    const canViewQuotes = usePermission(PERMISSIONS.QUOTATION_VIEW);
    const canManageQuotes = usePermission(PERMISSIONS.QUOTATION_MANAGE);
    const canViewOrders = usePermission(PERMISSIONS.SALES_ORDER_VIEW);
    const canManageReturns = usePermission(PERMISSIONS.SALES_RETURN_MANAGE);
    const canViewCustomers = usePermission(PERMISSIONS.CUSTOMER_VIEW);
    const canViewTarget = usePermission(PERMISSIONS.SALES_TARGET_VIEW); // S2
    const canAttendance = usePermission(PERMISSIONS.MENU_ATTENDANCE);
    const canLeave = usePermission(PERMISSIONS.MENU_LEAVE);

    const [loading, setLoading] = useState(true);
    const [quotes, setQuotes] = useState<{ rows: Quotation[]; total: number } | null>(null);
    const [orders, setOrders] = useState<{ rows: OrderSummary[]; total: number } | null>(null);
    const [backOrderCount, setBackOrderCount] = useState<number | null>(null);
    const [returns, setReturns] = useState<SalesReturnSummary[] | null>(null);
    const [myTarget, setMyTarget] = useState<SalesMyTarget | null>(null);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) { setLoading(false); return; }
        setLoading(true);
        const skipped = () => Promise.reject(new Error('skipped'));
        const [q, confirmed, partial, back, ret, tgt] = await Promise.allSettled([
            canViewQuotes ? QuotationService.listByInstitution(institutionId, 0, QUOTE_PAGE_SIZE) : skipped(),
            canViewOrders ? SalesOrderService.list(institutionId, { status: 'CONFIRMED', page: 0, size: QUOTE_PAGE_SIZE }) : skipped(),
            canViewOrders ? SalesOrderService.list(institutionId, { status: 'PARTIALLY_DELIVERED', page: 0, size: QUOTE_PAGE_SIZE }) : skipped(),
            canViewOrders ? SalesOrderService.backOrders(institutionId) : skipped(),
            canManageReturns ? SalesReturnService.list(institutionId, { status: 'DRAFT' }) : skipped(),
            canViewTarget ? SalesTargetService.my(institutionId) : skipped(),   // S2
        ]);
        setQuotes(q.status === 'fulfilled' ? { rows: q.value.content, total: q.value.totalElements } : null);
        if (confirmed.status === 'fulfilled' && partial.status === 'fulfilled') {
            setOrders({
                rows: [...confirmed.value.content, ...partial.value.content],
                total: confirmed.value.totalElements + partial.value.totalElements,
            });
        } else {
            setOrders(null);
        }
        setBackOrderCount(back.status === 'fulfilled' ? back.value.length : null);
        setReturns(ret.status === 'fulfilled' ? ret.value : null);
        setMyTarget(tgt.status === 'fulfilled' ? tgt.value : null);
        setLoading(false);
    }, [canViewQuotes, canViewOrders, canManageReturns, canViewTarget]);

    useEffect(() => { load(); }, [load]);

    const quoteStats = useMemo(() => {
        if (!quotes) return null;
        const byStatus = (s: QuotationStatus) => quotes.rows.filter(r => r.status === s);
        const sent = byStatus('SENT');
        const draft = byStatus('DRAFT');
        const won = quotes.rows.filter(r =>
            (r.status === 'CONFIRMED' || r.status === 'CONVERTED') && isThisMonth(r.confirmedAt || r.quotationDate));
        const chart = (Object.keys(STATUS_LABELS) as QuotationStatus[])
            .map(s => ({ label: STATUS_LABELS[s], value: byStatus(s).length }))
            .filter(d => d.value > 0);
        return {
            open: sent.length + draft.length,
            sent, draft, chart,
            sentValue: sent.reduce((s, r) => s + (r.totalAmount ?? 0), 0),
            wonCount: won.length,
            wonValue: won.reduce((s, r) => s + (r.totalAmount ?? 0), 0),
            truncated: quotes.total > QUOTE_PAGE_SIZE,
        };
    }, [quotes]);

    // Soonest requested delivery first; orders without a date go last.
    const toDeliver = useMemo(() => {
        if (!orders) return null;
        return [...orders.rows].sort((a, b) =>
            (a.requestedDeliveryDate || '9999').localeCompare(b.requestedDeliveryDate || '9999'));
    }, [orders]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
    }

    const hasAny = !!quoteStats || !!orders || backOrderCount !== null || !!returns;

    return (
        <Box data-help-page="sales.dashboard.rep">
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }} data-help-section="sales.dashboard.shortcuts">
                <Typography variant="h5" fontWeight="bold">Sales Dashboard</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {canManageQuotes && <Button component={RouterLink} to="/sales/quotations/new" size="small" variant="contained">New quotation</Button>}
                    {canViewQuotes && <Button component={RouterLink} to="/sales/quotations" size="small" variant="outlined">Quotations</Button>}
                    {canViewOrders && <Button component={RouterLink} to="/sales/orders" size="small" variant="outlined">Sales orders</Button>}
                    {canViewCustomers && <Button component={RouterLink} to="/sales/customers" size="small" variant="outlined">Customers</Button>}
                    {canAttendance && <Button component={RouterLink} to="/attendance" size="small" variant="text">My attendance</Button>}
                    {canLeave && <Button component={RouterLink} to="/leave-applications" size="small" variant="text">Apply for leave</Button>}
                </Stack>
            </Stack>

            {quoteStats?.truncated && (
                <Alert severity="info" sx={{ mb: 2 }} data-help-section="sales.dashboard.notice">
                    Quotation figures use the latest {QUOTE_PAGE_SIZE} of {fmtInt(quotes!.total)} quotations.
                </Alert>
            )}

            {!hasAny && <Alert severity="warning" sx={{ mb: 2 }} data-help-section="sales.dashboard.notice">Sales figures are unavailable right now.</Alert>}

            {hasAny && (
                <Paper data-help-section="sales.dashboard.kpi" variant="outlined" sx={{ p: 1, mb: 2 }}>
                    <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                        {quoteStats && (
                            <>
                                <StatTile data-help="sales.dashboard.tile.openQuotes" label="Open quotations" value={fmtInt(quoteStats.open)} sub="Draft and sent" />
                                <StatTile data-help="sales.dashboard.tile.sentValue" label="Quotes sent value" unit="৳" value={formatMoney(quoteStats.sentValue)} sub="Waiting on the customer" />
                                <StatTile data-help="sales.dashboard.tile.wonMonth" label="Won this month" value={fmtInt(quoteStats.wonCount)} tone={quoteStats.wonCount > 0 ? 'good' : 'default'}
                                    sub={`${formatMoney(quoteStats.wonValue)} confirmed or converted`} />
                            </>
                        )}
                        {orders && <StatTile data-help="sales.dashboard.tile.openOrders" label="Open orders" value={fmtInt(orders.total)} sub="Confirmed or part-delivered" />}
                        {backOrderCount !== null && (
                            <StatTile data-help="sales.dashboard.tile.backOrders" label="Back-orders" value={fmtInt(backOrderCount)} tone={backOrderCount > 0 ? 'warning' : 'default'} sub="Order lines not yet delivered" />
                        )}
                        {returns && <StatTile data-help="sales.dashboard.tile.draftReturns" label="Draft returns" value={fmtInt(returns.length)} tone={returns.length > 0 ? 'warning' : 'default'} sub="Customer returns" />}
                        {myTarget && myTarget.target !== null && (
                            <StatTile data-help="sales.dashboard.tile.myTarget" label="My target" unit="৳" value={formatMoney(myTarget.target)}
                                sub={`${formatMoney(myTarget.actual)} sold${myTarget.achievementPct !== null ? ` · ${myTarget.achievementPct.toFixed(1)}%` : ''}`}
                                tone={myTarget.achievementPct !== null && myTarget.achievementPct >= 100 ? 'good' : 'default'}
                                meter={myTarget.achievementPct ?? undefined} target={myTarget.target} targetLabel="This month" />
                        )}
                    </Stack>
                </Paper>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2, minmax(0,1fr))' }, gap: 2, mb: 2 }}>
                {quoteStats && (
                    <>
                        <Section data-help-section="sales.dashboard.byStatus" title="Quotations by status" subtitle="Latest quotations">
                            {quoteStats.chart.length === 0 ? <Empty text="No quotations yet." /> : <HBars data={quoteStats.chart} vFmt={fmtInt} />}
                        </Section>
                        <Section data-help-section="sales.dashboard.waiting" title="Waiting on the customer" subtitle={`${fmtInt(quoteStats.sent.length)} sent quotations`}>
                            {quoteStats.sent.length === 0 ? <Empty text="No sent quotations waiting." /> : <QuotationList rows={quoteStats.sent} testId="sent" />}
                        </Section>
                        <Section data-help-section="sales.dashboard.drafts" title="Draft quotations" subtitle={`${fmtInt(quoteStats.draft.length)} not yet sent`}>
                            {quoteStats.draft.length === 0 ? <Empty text="No draft quotations." /> : <QuotationList rows={quoteStats.draft} testId="draft" />}
                        </Section>
                    </>
                )}
                {toDeliver && orders && (
                    <Section data-help-section="sales.dashboard.toDeliver" title="Orders to deliver" subtitle={`${fmtInt(orders.total)} confirmed or part-delivered`}>
                        {toDeliver.length === 0 ? <Empty text="No orders waiting for delivery." /> : (
                            <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                                {toDeliver.slice(0, LIST_LIMIT).map(o => (
                                    <Stack key={o.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid={`order-${o.id}`}>
                                        <Link component={RouterLink} to={`/sales/orders/${o.id}`} underline="hover" sx={{ fontWeight: 600 }}>
                                            {o.orderNo || 'Order'}
                                        </Link>
                                        <Typography variant="body2" color="text.secondary">{o.customerName}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="body2" color={o.overdue ? 'error' : 'text.secondary'}>
                                            {o.requestedDeliveryDate ? `${o.overdue ? 'overdue · ' : 'due '}${formatDate(o.requestedDeliveryDate)}` : formatMoney(o.totalAmount)}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Section>
                )}
                {returns && (
                    <Section data-help-section="sales.dashboard.returns" title="Customer returns" subtitle={`${fmtInt(returns.length)} draft returns`}>
                        {returns.length === 0 ? <Empty text="No draft returns." /> : (
                            <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                                {returns.slice(0, LIST_LIMIT).map(r => (
                                    <Stack key={r.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid={`return-${r.id}`}>
                                        <Link component={RouterLink} to={`/sales/returns/${r.id}`} underline="hover" sx={{ fontWeight: 600 }}>
                                            {r.returnNo || 'Return'}
                                        </Link>
                                        <Typography variant="body2" color="text.secondary">{r.customerName}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="body2" color="text.secondary">{r.returnDate ? formatDate(r.returnDate) : ''}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Section>
                )}
            </Box>
            <MyBreakdownsDashSection />
            <MyWidgetsSection boardKey="sales-home" />
        </Box>
    );
};

export default SalesRepDashboard;
